import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";

import type { LedgerLeg, LedgerOperationType } from "../types/paymentsLedger.types";

/**
 * Double-entry ledger of the payments runtime. Backend contract, verified against coloca-nest
 * (`payment-ledger.internal.controller.ts` + `payment-ledger.internal.service.ts`):
 *
 * `GET /internal/payments/ledger/entries?operation_type=<enum>&operation_id=<uuid>`
 *
 * - Guard: `@JwtAuth({ permissions: ['payments:read'], userTypes: [INTERNAL] })` — a missing permission throws
 *   Unauthorized (**401**), an ORGANIZATION_MEMBER gets **403**. Both must latch as no-access.
 * - Both query params are REQUIRED, and they are the ONLY accepted ones: the app runs a global
 *   `ValidationPipe({ whitelist: true, forbidNonWhitelisted: true })`, so adding `limit`, `skip` or any other
 *   param answers **400**. There is no pagination and no `total` — the response is a bare array.
 * - Ordered `seq ASC` with the `account` relation eagerly joined (`scope_organization` is NOT joined: only
 *   `scope_organization_id` comes back).
 * - No tenancy filter at all: the endpoint is INTERNAL-only and takes any `operation_id`, cross-organization.
 * - An unknown `operation_id` is NOT a 404 — it answers `200 []`. "No entries" is a real state (a payin whose
 *   QR was minted and never paid has none), so the UI must render it as such and never as an error.
 *
 * ⚠️ Reversals live under a DIFFERENT `operation_type`. `LedgerService.reverse` inserts the mirror entry with
 * `operation_type = REVERSE` and the original `operation_id`, so a query for `PAYOUT` silently omits them
 * even though the endpoint's Swagger summary promises "reserve, settle/confirm, reversals". Callers must read
 * both types and merge by `seq` — that is what `useOperationLedger` does.
 */
export class PaymentsLedgerService {
  private static BASE_URL = "/internal/payments/ledger/entries";

  /** Legs posted under ONE `operation_type` for one operation, oldest first. */
  static async listByOperation(
    operationType: LedgerOperationType,
    operationId: string,
  ): Promise<AxiosResponse<LedgerLeg[]>> {
    const query = new URLSearchParams({ operation_type: operationType, operation_id: operationId });
    return HttpApiService.get<LedgerLeg[]>(`${this.BASE_URL}?${query}`);
  }
}
